import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";

import FormContainer from "@/pages/Public/components/FormContainer";

import { Icon, LoadingOverlay } from "@/components";

import {
	AnswerType,
	CompanyBalance,
	CompanyRevenue,
	CompanySize,
	Industry,
} from "@/types/questionnaire";

import BusinessInfoStepIndicator from "./BusinessInfoStepIndicator";
import BusinessInfoStepBody from "./BusinessInfoStepBody";

interface BusinessInfoStepsProps {
	currentStepIndex: number;
	industries: Industry[];
	companySizes: CompanySize[];
	companyRevenues: CompanyRevenue[];
	companyBalances: CompanyBalance[];
	currentAnswers: AnswerType[];
	handleNext: (currentAnswers: AnswerType[]) => void;
	handleBack: (currentAnswers: AnswerType[] | null) => void;
	isLoading: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
}

const stepContent = [
	{
		titleKey: "questionnaire.business.industry.title",
		descriptionKey: "questionnaire.business.industry.description",
	},
	{
		titleKey: "questionnaire.business.size.title",
		descriptionKey: "questionnaire.business.size.description",
	},
	{
		titleKey: "questionnaire.business.turnover.title",
		descriptionKey: "questionnaire.business.turnover.description",
	},
	{
		titleKey: "questionnaire.business.balance.title",
		descriptionKey: "questionnaire.business.balance.description",
	},
];

export default function BusinessInfoSteps({
	currentStepIndex,
	industries,
	companySizes,
	companyRevenues,
	companyBalances,
	currentAnswers,
	handleBack,
	handleNext,
	isLoading,
	formError,
}: BusinessInfoStepsProps) {
	const { t, ready } = useTranslation();
	const ts = (key: string, options = {}) => t(`questionnaire.business.progress.${key}`, options);

	const title = t(stepContent[currentStepIndex].titleKey);
	const description = t(stepContent[currentStepIndex].descriptionKey);

	const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>([]);
	const [selectedCompanySizeId, setSelectedCompanySizeId] = useState<number | null>(null);
	const [selectedCompanyRevenueId, setSelectedCompanyRevenueId] = useState<number | null>(null);
	const [selectedCompanyBalanceId, setSelectedCompanyBalanceId] = useState<number | null>(null);
	const [answer, setAnswer] = useState<AnswerType | null>(currentAnswers[currentStepIndex] ?? null);

	const totalSteps = 4;

	useEffect(() => {
		const current = currentAnswers[currentStepIndex];
		const currentValue = JSON.stringify(current?.values ?? []);
		const answerValue = JSON.stringify(answer?.values ?? []);

		const isState = currentValue !== answerValue;

		if (isState && (!answer || answer?.values?.length === 0)) {
			setAnswer(current ?? null);
		}
	}, [answer, answer?.values, currentAnswers, currentStepIndex]);

	if (isLoading && !ready) {
		return <LoadingOverlay />;
	}

	return (
		<div className="h-fit mt-24 mb-24 space-y-4 mx-auto max-w-[100%] ">
			<BusinessInfoStepIndicator currentStepIndex={currentStepIndex} totalSteps={totalSteps} />
			<FormContainer
				changeActionsDirection
				actionContainerClassName="flex gap-2 !space-y-0 "
				description={description}
				descriptionClass={answer && answer?.values?.length > 0 ? "!text-[#4680FC]" : ""}
				disableSecondaryButton={currentStepIndex === 0}
				formContainerClassName="h-fit "
				formError={formError}
				formHeader={
					<p className="text-[14px] font-medium text-primary-gray-lighter mb-6">
						{ts("step", {
							currentStep: currentStepIndex + 1,
							totalLength: totalSteps,
						})}
					</p>
				}
				handleSubmit={() => {
					if (answer && Array.isArray(answer.values) && answer.values.length > 0) {
						const updatedAnswers = [...currentAnswers];

						updatedAnswers[currentStepIndex] = answer;
						handleNext(updatedAnswers);

						if (currentStepIndex < totalSteps - 1) {
							setAnswer(null);
						}
					}
				}}
				isLoading={isLoading}
				secondaryButtonAction={() => {
					const updatedAnswers = [...currentAnswers];

					if (answer) {
						updatedAnswers[currentStepIndex] = answer;
						handleBack(updatedAnswers);
					} else {
						handleBack(null);
					}

					if (currentStepIndex > 0) {
						setAnswer(null);
					}
				}}
				secondaryButtonText={
					<p className="text-[14px] text-primary-blue-dark font-medium flex items-center justify-center gap-2">
						<Icon className="w-4 h-3" icon={faArrowLeft} />
						{ts("back")}
					</p>
				}
				secondaryButtonType="button"
				showLogo={false}
				submitButtonText={
					<p className="text-[14px] text-white font-medium flex items-center justify-center gap-2">
						{ts(currentStepIndex === totalSteps - 1 ? "finish" : "next")}
						<Icon className="w-4 h-3 text-white" icon={faArrowRight} />
					</p>
				}
				submitButtonType="button"
				title={title}
			>
				<BusinessInfoStepBody
					companyBalances={companyBalances}
					companyRevenues={companyRevenues}
					companySizes={companySizes}
					currentStepIndex={currentStepIndex}
					industries={industries}
					selectedCompanyBalanceId={selectedCompanyBalanceId}
					selectedCompanyRevenueId={selectedCompanyRevenueId}
					selectedCompanySizeId={selectedCompanySizeId}
					selectedIndustryIds={selectedIndustryIds}
					setAnswer={setAnswer}
					setSelectedCompanyBalanceId={setSelectedCompanyBalanceId}
					setSelectedCompanyRevenueId={setSelectedCompanyRevenueId}
					setSelectedCompanySizeId={setSelectedCompanySizeId}
					setSelectedIndustryIds={setSelectedIndustryIds}
				/>
			</FormContainer>
		</div>
	);
}
